/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './report-template-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {ADN_GRIDSTER_CONFIG} from '../config/gridster-config';
import {TOOLBAR_BUTTONS} from '../common/directives/notes/notes-constants';
import {ADN_REPORT_COMPONENTS, ADN_REPORT_TYPES} from '../report/report-types';
import type {ReportTemplateGridsterResource} from './report-template-gridster-item.component';
import {ReportTemplateWidgetModalComponent} from './report-template-widget-modal.component';
import {OgTranslateService} from '../common/filters/og-translate.service';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {
  applyLoadedReportTranslationDefaults,
  buildReportDateFormats,
  createEmptyReportTypeComponents,
  getAvailableReportLanguages,
  getReportTemplateTeamQueryParams,
  getReportTemplateTypes,
  initReportTemplateComponent,
  initReportTemplateModel,
  normalizeReportTemplateComponent,
  prepareReportTemplateForSubmit,
  restoreReportTemplateComponentsAfterReload,
  timeChangeReportComponent
} from './report-template-commons';
import {ReportTranslationResource} from '../../components/api/resources/resource/report-translation.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {ReportTranslationDefaultsResource} from '../../components/api/resources/resource/report-translation-defaults';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-report-template-edit-page',
  template
})
export class ReportTemplateEditPageComponent extends AdminResourceEditPageBase {
  readonly widgets = ADN_REPORT_COMPONENTS;
  readonly toolbarButtons = TOOLBAR_BUTTONS;
  readonly dateFormats = buildReportDateFormats();

  showTeams = false;
  isAxAdvertiserAdmin = false;
  isAxAdvertiser = false;
  logoSpecify = false;
  showHeaderContent = false;
  showFooterContent = false;
  showTranslations = false;

  teams: any[] = [];
  reportTranslations: any[] = [];
  reportTypes: string[] = [];
  availableLangs: Record<string, string> = {};
  translations: Record<string, Record<string, string>> = {};
  translationFields: Record<string, string> = {};

  reportTypeComponents = createEmptyReportTypeComponents();
  gridsterOpts = _.merge({}, ADN_GRIDSTER_CONFIG.RESOURCES, {
    draggable: {
      stop: () => this.markExternalFormDirty()
    }
  });

  readonly emptyComponents: any[] = [];
  readonly gridsterResource: ReportTemplateGridsterResource = {
    model: null,
    widgets: this.widgets,
    toolbarButtons: this.toolbarButtons,
    dateFormats: this.dateFormats,
    isAxAdvertiser: false,
    removeComponent: (component) => this.removeComponent(component),
    timeChange: (component) => this.timeChange(component),
    markDirty: () => this.markExternalFormDirty()
  };

  private lastComponentId = 0;
  private savedModel: any;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(ReportTranslationResource) private readonly ReportTranslation: any,
    @Inject(ReportTranslationDefaultsResource) private readonly reportTranslationDefaultsResource: any,
    private readonly ogTranslate: OgTranslateService,
    private readonly angularModalService: AngularModalService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
  }

  getCurrentComponents(): any[] {
    const reportType = this.editingModel?.reportType;
    if (!reportType) {
      return this.emptyComponents;
    }
    return this.reportTypeComponents[reportType]?.components || this.emptyComponents;
  }

  compareTeams(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  compareReportTranslations(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  onReportTypeChange(): void {
    this.markExternalFormDirty();
  }

  updateTranslations(langKey: string): void {
    if (!this.translations.en) {
      return;
    }
    _.forEach(this.translations.en, (val, key) => {
      this.editingModel.parameters[key] = (this.translations[langKey] || this.translations.en)[key];
    });
    this.syncTranslationFields();
    this.markExternalFormDirty();
  }

  protected afterInitEditingModel(): void {
    const perms = this.LocalUserPermissions.getAllVisPermissions();
    this.isAxAdvertiserAdmin = perms.AX_ADVERTISER_ADMIN === true || perms.AX_ADVERTISER_TEAM_ADMIN === true;
    this.isAxAdvertiser = perms.AX_ADVERTISER === true;
    this.gridsterResource.model = this.editingModel;
    this.gridsterResource.isAxAdvertiser = this.isAxAdvertiser;
    this.showTeams = this.LocalNetworkProfile.isPureMarketplacePlatform() || this.LocalNetworkProfile.isSuiPlatform();
    this.reportTypes = getReportTemplateTypes(this.LocalNetworkProfile.isSuiPlatform());
    this.availableLangs = getAvailableReportLanguages(this.LocalNetworkProfile.getNetworkId());

    this.savedModel = _.cloneDeep(this.model);
    this.reportTypeComponents = createEmptyReportTypeComponents();
    this.logoSpecify = !!this.editingModel.logo;
    initReportTemplateModel(
      this.editingModel,
      this.savedModel,
      this.reportTypeComponents,
      () => ++this.lastComponentId
    );
    this.resetComponentIds();

    this.loadTeams(perms);
    this.loadReportTranslations();
    this.loadTranslationDefaults();
    this.syncTranslationFields();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj) {
      this.savedModel = _.cloneDeep(saveObj);
      if (!this.savedModel.components?.length && this.editingModel.components?.length) {
        this.savedModel.components = _.cloneDeep(this.editingModel.components);
      }
      this.reloadReportTypeComponentsFromModel();
    }
  }

  protected onBeforeSubmit(): void {
    prepareReportTemplateForSubmit(
      this.editingModel,
      this.reportTypeComponents,
      this.logoSpecify,
      (key: string, defaultValue?: string) => this.ogTranslate.transform(key, defaultValue)
    );
  }

  openAddComponents(): void {
    const reportType = ADN_REPORT_TYPES[this.editingModel.reportType];
    if (!reportType) {
      return;
    }
    const components = _.filter(reportType.components, (component) => {
      return this.editingModel.reportFormat !== 'CSV' || component.csv;
    });

    const modalRef = this.angularModalService.show(ReportTemplateWidgetModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {components}
    });
    (modalRef.content as ReportTemplateWidgetModalComponent).onSaved.subscribe((selections: Record<string, any>) => {
      if (!selections) {
        return;
      }
      _.forEach(selections, (component) => {
        const pushedComponent = {
          reportComponent: {id: component.id},
          parameters: _.cloneDeep(component.parameters),
          uniqueId: ++this.lastComponentId
        };
        initReportTemplateComponent(pushedComponent);
        this.reportTypeComponents[this.editingModel.reportType].components.push(pushedComponent);
      });
      this.markExternalFormDirty();
    });
  }

  private loadTeams(perms: any): void {
    this.Team.query(getReportTemplateTeamQueryParams(perms)).$promise.then((page: any) => {
      this.teams = page.results || [];
    });
  }

  private loadReportTranslations(): void {
    this.ReportTranslation.query().$promise.then((data: any) => {
      this.reportTranslations = data.results || [];
    });
  }

  private loadTranslationDefaults(): void {
    if (this.translations.en) {
      return;
    }
    this.reportTranslationDefaultsResource.get().then((transDefaults: Record<string, Record<string, string>>) => {
      this.translations = transDefaults || {en: {}};
      applyLoadedReportTranslationDefaults(
        this.editingModel,
        this.translations,
        (key: string, defaultValue?: string) => this.ogTranslate.transform(key, defaultValue)
      );
      this.syncTranslationFields();
    });
  }

  private syncTranslationFields(): void {
    this.translationFields = this.translations.en ? {...this.translations.en} : {};
  }

  private reloadReportTypeComponentsFromModel(): void {
    const reportType = this.editingModel?.reportType;
    if (!reportType) {
      return;
    }
    const components = this.editingModel.components || this.savedModel?.components;
    if (!components?.length) {
      return;
    }
    const previousComponents = this.reportTypeComponents[reportType].components || [];
    this.reportTypeComponents[reportType].components = restoreReportTemplateComponentsAfterReload(
      _.cloneDeep(components),
      previousComponents,
      () => ++this.lastComponentId
    );
    _.forEach(this.reportTypeComponents[reportType].components, (component) => {
      component.parameters = component.parameters || {};
      if (typeof component.reportComponent === 'string') {
        component.reportComponent = {id: component.reportComponent};
      }
      if (!_.isFinite(component.uniqueId)) {
        component.uniqueId = ++this.lastComponentId;
      }
      normalizeReportTemplateComponent(component);
    });
    this.resetComponentIds();
  }

  private removeComponent(component: any): void {
    _.pull(this.reportTypeComponents[this.editingModel.reportType].components, component);
    this.markExternalFormDirty();
  }

  private timeChange(component: any): void {
    timeChangeReportComponent(component, this.dateFormats);
    this.markExternalFormDirty();
  }

  private resetComponentIds(): void {
    this.lastComponentId = 0;
    _.forEach(this.reportTypeComponents, (bucket) => {
      _.forEach(bucket.components, (component) => {
        if (_.isFinite(component.uniqueId) && component.uniqueId > this.lastComponentId) {
          this.lastComponentId = component.uniqueId;
        }
      });
    });
  }
}
